"use strict";

(function () {
  if (customElements.get("book-a-demo-v2")) return;

  class BookADemoV2 extends HTMLElement {
    // =================================================================
    // CONFIGURATION
    // =================================================================
    static MODAL_RESIZE_DELAY = 200;
    static DEFAULT_MAP_ZOOM = 5;
    static LOCATION_FOCUS_ZOOM = 15;
    static USER_LOCATION_ZOOM = 10;
    static MARKER_CANVAS_SIZE = 40;
    static DEFAULT_MAP_CENTER = { lat: 48.399, lng: 9.987 };

    // =================================================================
    // PRIVATE PROPERTIES
    // =================================================================
    #sectionId = null;
    #locations = [];
    #selectedDemoType = null;
    #selectedLocationHandle = null;

    // DOM Elements
    #form = null;
    #dataScript = null;
    #submitBtn = null;
    #demoTypeSelect = null;
    #countrySelect = null;
    #locationModal = null;
    #locationRadios = null;
    #locationCards = [];
    #locationsList = null;
    #noPhysicalLocationNotice = null;
    #selectedLocationDisplay = null;
    #mapContainer = null;
    #useMyLocationBtn = null;
    #mapSearchInput = null;

    // Map Properties
    #map = null;
    #markers = [];
    #userMarker = null;
    #geocoder = null;
    #mapInitialized = false;
    #markerImageUrl = null;

    #noPhysicalMsg = null;

    constructor() {
      super();
      this.#sectionId = this.dataset.sectionId;
      this.#markerImageUrl = this.dataset.markerImageUrl || null;
      this.#noPhysicalMsg = this.dataset.noPhysicalMsg || "No physical demo location is available for the selected region. Please choose a virtual demo instead.";
    }

    // =================================================================
    // LIFECYCLE METHODS
    // =================================================================

    /**
     * Called when the element is added to the document's DOM.
     */
    connectedCallback() {
      this.#setupElements();
      this.#parseAndIndexLocations();
      this.#renderCountryOptions();
      this.#renderLocationCards();
      this.#attachEventListeners();
      this.#syncPhysicalAvailability();
      this.#syncJordanEnglishOnlyLabels();
      this.#filterLocationOptions();
      this.#updateLocationDisplay();
      this.#updateSubmitButtonState();
    }

    /**
     * Called when the element is removed from the document's DOM.
     */
    disconnectedCallback() {
      this.#detachEventListeners();
    }

    // =================================================================
    // INITIALIZATION
    // =================================================================

    /**
     * Queries and stores references to all necessary DOM elements.
     */
    #setupElements() {
      const selector = (sel) => this.querySelector(sel);
      const selectorAll = (sel) => this.querySelectorAll(sel);

      this.#form = selector("form");
      this.#dataScript = selector(`#locations-${this.#sectionId}`);
      this.#submitBtn = selector(".bd-form__btn");
      this.#demoTypeSelect = selector(`#demoType-${this.#sectionId}`);
      this.#countrySelect = selector(`#country-${this.#sectionId}`);
      this.#locationModal = selector('[data-component="location-modal"]');
      this.#locationsList = selector(".bd-modal__locations");
      this.#noPhysicalLocationNotice = selector(
        '[data-component="no-physical-location"]',
      );
      this.#selectedLocationDisplay = selector(
        '[data-component="selected-location"]',
      );
      this.#mapContainer = selector(".bd-modal__map");
      this.#useMyLocationBtn = selector(".bd-modal__location-btn");
      this.#mapSearchInput = selector(`#map-search-${this.#sectionId}`);
      this.#locationRadios = selectorAll('input[name="location"]');
    }

    /**
     * Parses the location data from the JSON script tag embedded in the Liquid template.
     */
    #parseAndIndexLocations() {
      if (!this.#dataScript?.textContent) return;
      try {
        const json = JSON.parse(this.#dataScript.textContent);
        const rawLocations = json.locations || [];
        this.#locations = rawLocations.map((location, index) => ({
          ...location,
          displayIndex: index + 1,
          hasPhysical:
            !!location.url_physical &&
            !!String(location.address || "").trim() &&
            !!String(location.name || "").trim(),
        }));
      } catch (e) {
        console.error("Locations JSON parsing error:", e);
      }
    }

    #renderCountryOptions() {
      if (!this.#countrySelect) return;

      const currentValue = this.#countrySelect.value;
      const options = this.#locations.map((location) => ({
        value: location.country_handle,
        label: location.country,
      }));
      const marketCountry = ((window.Shopify && window.Shopify.country) || window.SHOP_COUNTRY || "").toUpperCase();
      const defaultCountryByMarket = {
        FR: "France",
        NL: "Netherlands",
        BE: "Belgium",
        AT: "Austria",
        CH: "Switzerland",
        IE: "Ireland",
      };
      const defaultCountryLabel = defaultCountryByMarket[marketCountry];
      const defaultOption = defaultCountryLabel
        ? options.find((option) => option.label === defaultCountryLabel)
        : null;

      this.#countrySelect.innerHTML =
        '<option value="" selected disabled>Select</option>' +
        options
          .map(
            (option) =>
              `<option value="${this.#escapeHtml(option.value)}">${this.#escapeHtml(option.label)}</option>`,
          )
          .join("");

      if (currentValue && options.some((option) => option.value === currentValue)) {
        this.#countrySelect.value = currentValue;
      } else if (defaultOption) {
        this.#countrySelect.value = defaultOption.value;
      }
    }

    #renderLocationCards() {
      if (!this.#locationsList) return;

      const locationColorScheme = this.dataset.locationColorScheme || "scheme-2";
      const physicalLocations = this.#locations.filter(
        (location) => location.hasPhysical,
      );

      const cardsMarkup = physicalLocations
        .map((location) => {
          const imageMarkup = location.image_url
            ? `
              <div class="bd-location__media-wrapper">
                <div class="bd-locattion__media media media--custom">
                  <img src="${this.#escapeAttribute(location.image_url)}" alt="${this.#escapeAttribute(location.name || "Location image")}" width="1200" height="1200" sizes="100vw">
                </div>
              </div>
            `
            : "";

          return `
            <div class="bd-location" data-country="${this.#escapeAttribute(location.country)}" data-country-handle="${this.#escapeAttribute(location.country_handle)}">
              <input
                type="radio"
                name="location"
                id="location-${this.#escapeAttribute(location.country_handle)}"
                value="${this.#escapeAttribute(location.country_handle)}"
                class="visually-hidden"
              >
              <label class="bd-location__wrapper color-${this.#escapeAttribute(locationColorScheme)}" for="location-${this.#escapeAttribute(location.country_handle)}">
                <div class="bd-location__checkbox">
                  <span class="svg-wrapper">✓</span>
                </div>
                ${imageMarkup}
                <div class="bd-location__inner">
                  <div class="bd-location__name">${this.#escapeHtml(location.name)}</div>
                  <div class="bd-location__address">${this.#escapeHtml(location.address)}</div>
                </div>
              </label>
            </div>
          `;
        })
        .join("");

      this.#locationsList.innerHTML = `
        <div class="bd-modal__empty hidden" data-component="no-physical-location">
          ${this.#noPhysicalMsg}
        </div>
        ${cardsMarkup}
      `;

      this.#noPhysicalLocationNotice = this.querySelector(
        '[data-component="no-physical-location"]',
      );
      this.#locationRadios = this.querySelectorAll('input[name="location"]');
      this.#locationCards = Array.from(this.querySelectorAll(".bd-location"));
    }

    /**
     * Attaches all necessary event listeners to the component's elements.
     */
    #attachEventListeners() {
      this.#demoTypeSelect?.addEventListener(
        "change",
        this.#handleDemoTypeChange,
      );
      this.#countrySelect?.addEventListener(
        "change",
        this.#handleCountryChange,
      );
      this.#form?.addEventListener("submit", this.#handleFormSubmit);
      this.#useMyLocationBtn?.addEventListener(
        "click",
        this.#handleUseMyLocation,
      );

      this.#locationRadios.forEach((radio) => {
        radio.addEventListener("change", this.#handleLocationChange);
      });

      // Add a click listener to the location container to check the inner radio button.
      this.#locationCards.forEach((item) => {
        item.addEventListener("click", () => {
          const radio = item.querySelector('input[type="radio"]');
          if (radio && !radio.checked) {
            radio.checked = true;
            radio.dispatchEvent(new Event("change", { bubbles: true }));
          }
        });
      });
    }

    /**
     * Cleans up event listeners when the component is removed from the DOM.
     */
    #detachEventListeners() {
      this.#demoTypeSelect?.removeEventListener(
        "change",
        this.#handleDemoTypeChange,
      );
      this.#countrySelect?.removeEventListener(
        "change",
        this.#handleCountryChange,
      );
      this.#form?.removeEventListener("submit", this.#handleFormSubmit);
      this.#useMyLocationBtn?.removeEventListener(
        "click",
        this.#handleUseMyLocation,
      );
      this.#locationRadios.forEach((radio) => {
        radio.removeEventListener("change", this.#handleLocationChange);
      });
    }

    // =================================================================
    // EVENT HANDLERS
    // =================================================================

    /**
     * Handles changes to the "Demo Type" dropdown.
     * @param {Event} e - The change event object.
     */
    #handleDemoTypeChange = (e) => {
      this.#selectedDemoType = e.target.value;
      this.#clearLocationSelection();
      this.#filterLocationOptions();

      if (this.#selectedDemoType === "physical") {
        this.#showLocationModal();
        this.#initMap(); // Initialize the map if not already done.
      } else {
        this.#hideLocationModal();
      }
      this.#updateSubmitButtonState();
    };

    /**
     * Handles changes to the "Country" dropdown.
     */
    #handleCountryChange = () => {
      this.#clearLocationSelection();
      this.#syncPhysicalAvailability();
      this.#syncJordanEnglishOnlyLabels();
      this.#filterLocationOptions();
      this.#updateSubmitButtonState();
    };

    /**
     * Handles changes to the location radio buttons.
     * @param {Event} e - The change event object.
     */
    #handleLocationChange = (e) => {
      if (!e.target.checked) return;
      this.#selectedLocationHandle = e.target.value;
      this.#updateLocationDisplay();
      this.#updateSubmitButtonState();

      // Pan the map to the selected marker.
      const marker = this.#markers.find(
        (m) => m.country_handle === this.#selectedLocationHandle,
      );
      if (marker) {
        this.#map.panTo(marker.getPosition());
        this.#map.setZoom(BookADemoV2.LOCATION_FOCUS_ZOOM);
        google.maps.event.trigger(marker, "click");
      }
    };

    /**
     * Handles the form submission, redirecting the user to the appropriate URL.
     * @param {Event} e - The submit event object.
     */
    #handleFormSubmit = (e) => {
      e.preventDefault();
      if (!this.#isFormValid()) return;

      const handle =
        this.#selectedDemoType === "physical"
          ? this.#selectedLocationHandle
          : this.#countrySelect.value;

      const location = this.#locations.find((l) => l.country_handle === handle);

      let url;
      if (this.#selectedDemoType === "physical") {
        url = location?.url_physical;
      } else if (this.#selectedDemoType === "virtual") {
        url = location?.url_virtual;
      } else if (this.#selectedDemoType === "quick_qa") {
        url = location?.url_quick_qa;
      } else if (this.#selectedDemoType === "post_sale") {
        url = location?.url_post_sale;
      }

      if (url) {
        window.location.href = url;
      } else if (this.dataset.urlFallback) {
        window.location.href = this.dataset.urlFallback;
      }
    };

    /**
     * Handles the "Use my location" button click.
     */
    #handleUseMyLocation = () => {
      if (!navigator.geolocation) {
        alert("Geolocation is not supported by your browser.");
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const userPos = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };

          this.#map.setCenter(userPos);
          this.#map.setZoom(BookADemoV2.USER_LOCATION_ZOOM);
          this.#userMarker?.setMap(null); // Remove old user marker.

          this.#userMarker = new google.maps.Marker({
            map: this.#map,
            position: userPos,
            icon: {
              path: google.maps.SymbolPath.CIRCLE,
              scale: 8,
              fillColor: "#4285F4",
              fillOpacity: 1,
              strokeColor: "#fff",
              strokeWeight: 2,
            },
          });
          this.#selectNearestMarker(userPos);
        },
        () => {
          alert("Unable to retrieve your location.");
        },
      );
    };

    // =================================================================
    // STATE & UI MANAGEMENT
    // =================================================================

    /**
     * Updates the text display for the selected location.
     */
    #updateLocationDisplay() {
      if (!this.#selectedLocationDisplay) return;

      const radio = this.querySelector(
        `input[value="${this.#selectedLocationHandle}"]`,
      );

      if (!radio) {
        this.#selectedLocationDisplay.textContent = "";
        return;
      }

      const wrap = radio.closest(".bd-location");
      const address =
        wrap?.querySelector(".bd-location__address")?.textContent.trim() || "";
      const name =
        wrap?.querySelector(".bd-location__name")?.textContent.trim() || "";
      this.#selectedLocationDisplay.textContent = address || name;
    }

    /**
     * Enables or disables the submit button based on form validity.
     */
    #updateSubmitButtonState() {
      if (!this.#submitBtn) return;
      this.#submitBtn.disabled = !this.#isFormValid();
    }

    /**
     * Checks if the form is valid for submission.
     * @returns {boolean} True if the form is valid.
     */
    #isFormValid() {
      if (!this.#selectedDemoType || !this.#countrySelect?.value) return false;
      // Valid if virtual/quick_qa/post_sale is chosen, or if physical is chosen and a location is selected.
      return (
        this.#selectedDemoType === "virtual" ||
        this.#selectedDemoType === "quick_qa" ||
        this.#selectedDemoType === "post_sale" ||
        (this.#selectedDemoType === "physical" &&
          !!this.#selectedLocationHandle)
      );
    }

    /**
     * Shows the location modal.
     */
    #showLocationModal() {
      this.#locationModal?.classList.remove("hidden");
      // Trigger a map resize after the modal animation to ensure correct rendering.
      setTimeout(() => {
        if (this.#map) {
          google.maps.event.trigger(this.#map, "resize");
        }
      }, BookADemoV2.MODAL_RESIZE_DELAY);
    }

    /**
     * Hides the location modal and clears the selected location display.
     */
    #hideLocationModal() {
      this.#locationModal?.classList.add("hidden");
      if (this.#selectedLocationDisplay) {
        this.#selectedLocationDisplay.textContent = "";
      }
    }

    // =================================================================
    // MAP LOGIC
    // =================================================================

    /**
     * Initializes the Google Map instance.
     */
    #initMap() {
      if (this.#mapInitialized || !window.google?.maps) return;
      this.#mapInitialized = true;
      this.#geocoder = new google.maps.Geocoder();

      this.#map = new google.maps.Map(this.#mapContainer, {
        center: BookADemoV2.DEFAULT_MAP_CENTER,
        zoom: BookADemoV2.DEFAULT_MAP_ZOOM,
        mapTypeControl: false,
        streetViewControl: false,
      });

      // Use addListenerOnce to run setup logic only the *first* time the map is ready.
      google.maps.event.addListenerOnce(this.#map, "idle", () => {
        this.#initPlaceAutocomplete();
        this.#plotAllMarkers();
      });
    }

    /**
     * Initializes the Google Places Autocomplete search input.
     */
    async #initPlaceAutocomplete() {
      if (!this.#mapSearchInput || !window.google?.maps) return;

      try {
        await google.maps.importLibrary("places");
        const placeAutocomplete =
          new google.maps.places.PlaceAutocompleteElement({});
        placeAutocomplete.placeholder = "Enter a postal code or address";

        this.#mapSearchInput.innerHTML = "";
        this.#mapSearchInput.appendChild(placeAutocomplete);

        placeAutocomplete.addEventListener("gmp-select", async (event) => {
          const placePrediction = event.placePrediction;
          if (!placePrediction) return;

          try {
            const place = await placePrediction.toPlace();
            await place.fetchFields({ fields: ["location", "viewport"] });

            if (!place.location || !this.#map) return;

            // Force the map to resize, then center and zoom on the selected location.
            google.maps.event.trigger(this.#map, "resize");
            this.#map.setCenter(place.location);
            if (place.viewport) {
              this.#map.fitBounds(place.viewport);
            } else {
              this.#map.setZoom(BookADemoV2.LOCATION_FOCUS_ZOOM);
            }
          } catch (error) {
            console.error("Error processing selected place:", error);
          }
        });
      } catch (error) {
        console.error("Failed to initialize PlaceAutocompleteElement:", error);
      }
    }

    /**
     * Geocodes and plots markers for all store locations.
     */
    #plotAllMarkers() {
      this.#locations.forEach((loc) => {
        if (!loc.hasPhysical || !loc.address) return;

        this.#geocoder.geocode(
          { address: loc.address },
          async (results, status) => {
            if (status !== "OK" || !results?.[0]) {
              console.error(`Geocode failed for "${loc.address}": ${status}`);
              return;
            }

            const position = results[0].geometry.location;
            const iconData = await this.#createNumberedMarkerIcon(
              loc.displayIndex,
            );

            const marker = new google.maps.Marker({
              map: this.#map,
              position,
              title: loc.name,
              icon: iconData, // This will be null if no custom icon is used.
            });
            marker.country_handle = loc.country_handle; // Attach custom data.
            marker.country = loc.country;

            const infoWindow = new google.maps.InfoWindow({
              content: `<strong>${loc.name}</strong><br>${loc.address}`,
            });

            marker.addListener("click", () => {
              infoWindow.open({ map: this.#map, anchor: marker });
              // Sync the radio button selection with the marker click.
              const radio = this.querySelector(
                `#location-${loc.country_handle}`,
              );
              if (radio && !radio.checked) {
                radio.checked = true;
                radio.dispatchEvent(new Event("change", { bubbles: true }));
              }
            });
            this.#markers.push(marker);
            this.#syncMarkerVisibility();
          },
        );
      });
    }

    #clearLocationSelection() {
      this.#selectedLocationHandle = null;
      this.#locationRadios.forEach((radio) => {
        radio.checked = false;
      });
      this.#updateLocationDisplay();
    }

    #getSelectedCountryHandle() {
      return this.#countrySelect?.value || "";
    }

    #getPhysicalLocationsForSelectedCountry() {
      const selectedCountryHandle = this.#getSelectedCountryHandle();
      return this.#locations.filter((location) => {
        if (!location.hasPhysical) return false;
        if (!selectedCountryHandle) return true;
        return location.country_handle === selectedCountryHandle;
      });
    }

    #syncJordanEnglishOnlyLabels() {
      if (!this.#demoTypeSelect || !this.#countrySelect) return;
      const selectedCountryHandle = this.#getSelectedCountryHandle();
      const selectedLocation = this.#locations.find(
        (location) => location.country_handle === selectedCountryHandle,
      );
      const isNetherlands = selectedLocation?.country === "Netherlands";
      const routesToJordan = /cal\.futuremvmt\.com\/jordan\//i.test(
        `${selectedLocation?.url_virtual || ""} ${selectedLocation?.url_quick_qa || ""}`,
      );
      const shouldShowEnglishOnly = !!selectedLocation && routesToJordan && !isNetherlands;

      ["virtual", "quick_qa"].forEach((value) => {
        const option = this.#demoTypeSelect.querySelector(`option[value="${value}"]`);
        if (!option) return;
        if (!option.dataset.baseLabel) option.dataset.baseLabel = option.textContent.trim();
        option.textContent = shouldShowEnglishOnly
          ? `${option.dataset.baseLabel} - English only`
          : option.dataset.baseLabel;
      });
    }
    #syncPhysicalAvailability() {
      const physicalOption = this.#demoTypeSelect?.querySelector(
        'option[value="physical"]',
      );
      if (!physicalOption) return;

      const hasPhysicalLocation = this.#getPhysicalLocationsForSelectedCountry()
        .length > 0;
      const selectedCountryHandle = this.#getSelectedCountryHandle();

      // Only disable the physical option — virtual, quick_qa, and post_sale are always available.
      physicalOption.disabled = !!selectedCountryHandle && !hasPhysicalLocation;

      if (
        this.#selectedDemoType === "physical" &&
        selectedCountryHandle &&
        !hasPhysicalLocation
      ) {
        this.#selectedDemoType = null;
        this.#demoTypeSelect.value = "";
        this.#hideLocationModal();
      }
    }

    #filterLocationOptions() {
      const selectedCountryHandle = this.#getSelectedCountryHandle();
      let visibleCount = 0;

      this.#locationCards.forEach((item) => {
        const matches =
          !selectedCountryHandle ||
          item.dataset.countryHandle === selectedCountryHandle;
        item.hidden = !matches;
        item.classList.toggle("hidden", !matches);
        if (!matches) {
          const radio = item.querySelector('input[type="radio"]');
          if (radio) radio.checked = false;
        } else {
          visibleCount += 1;
        }
      });

      if (this.#noPhysicalLocationNotice) {
        const shouldShowEmptyState =
          !!selectedCountryHandle && visibleCount === 0;
        this.#noPhysicalLocationNotice.hidden = !shouldShowEmptyState;
        this.#noPhysicalLocationNotice.classList.toggle(
          "hidden",
          !shouldShowEmptyState,
        );
      }

      this.#syncMarkerVisibility();
    }

    #syncMarkerVisibility() {
      if (!this.#map || !this.#markers.length) return;

      const selectedCountryHandle = this.#getSelectedCountryHandle();
      const visibleMarkers = this.#markers.filter((marker) => {
        return !selectedCountryHandle || marker.country_handle === selectedCountryHandle;
      });

      this.#markers.forEach((marker) => {
        const isVisible =
          !selectedCountryHandle || marker.country_handle === selectedCountryHandle;
        marker.setVisible(isVisible);
      });

      if (!visibleMarkers.length) {
        this.#map.setCenter(BookADemoV2.DEFAULT_MAP_CENTER);
        this.#map.setZoom(BookADemoV2.DEFAULT_MAP_ZOOM);
        return;
      }

      if (visibleMarkers.length === 1) {
        this.#map.panTo(visibleMarkers[0].getPosition());
        this.#map.setZoom(BookADemoV2.LOCATION_FOCUS_ZOOM);
        return;
      }

      const bounds = new google.maps.LatLngBounds();
      visibleMarkers.forEach((marker) => bounds.extend(marker.getPosition()));
      this.#map.fitBounds(bounds, 48);
    }

    #escapeHtml(value) {
      return String(value || "")
        .replaceAll("&", "&amp;")
        .replaceAll("<", "&lt;")
        .replaceAll(">", "&gt;")
        .replaceAll('"', "&quot;")
        .replaceAll("'", "&#39;");
    }

    #escapeAttribute(value) {
      return this.#escapeHtml(value);
    }



    /**
     * Creates a custom marker icon using the Canvas API to draw a number on an image.
     * @param {number} number - The number to draw on the marker.
     * @returns {Promise<google.maps.Icon|null>} A promise that resolves with the marker icon object or null.
     */
    #createNumberedMarkerIcon(number) {
      return new Promise((resolve) => {
        if (!this.#markerImageUrl) return resolve(null);

        const canvas = document.createElement("canvas");
        const size = BookADemoV2.MARKER_CANVAS_SIZE;
        canvas.width = size;
        canvas.height = size;
        const context = canvas.getContext("2d");
        const image = new Image();
        image.crossOrigin = "Anonymous";
        image.src = this.#markerImageUrl;

        image.onload = () => {
          context.drawImage(image, 0, 0, size, size);
          context.font = "bold 10px Arial";
          context.fillStyle = "white";
          context.textAlign = "center";
          context.textBaseline = "middle";
          context.fillText(number.toString(), size / 2, size / 2 + 1);

          resolve({
            url: canvas.toDataURL(),
            scaledSize: new google.maps.Size(size, size),
            anchor: new google.maps.Point(size / 2, size / 2),
          });
        };

        image.onerror = () => {
          console.error("Failed to load custom marker image.");
          resolve(null);
        };
      });
    }

    /**
     * Finds and selects the nearest store marker to the user's current position.
     * @param {{lat: number, lng: number}} userPos - The user's latitude and longitude.
     */
    #selectNearestMarker(userPos) {
      if (this.#markers.length === 0) return;

      const userLatLng = new google.maps.LatLng(userPos.lat, userPos.lng);

      // Use reduce to find the marker with the minimum distance.
      const closest = this.#markers.reduce(
        (acc, marker) => {
          const distance =
            google.maps.geometry.spherical.computeDistanceBetween(
              userLatLng,
              marker.getPosition(),
            );
          return distance < acc.distance ? { distance, marker } : acc;
        },
        { distance: Infinity, marker: null },
      );

      if (closest.marker) {
        google.maps.event.trigger(closest.marker, "click");
      }
    }
  }

  customElements.define("book-a-demo-v2", BookADemoV2);
})();
